import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Table, Button, Modal } from "react-bootstrap";
import { jsPDF } from "jspdf";
import Loader from "../../UI/Loader";
import "jspdf-autotable";
import classes from "./CheckUpReport.module.css";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import { useNavigate } from "react-router-dom";
import Filters from "./Filters";
import { toast } from "react-toastify";
import { FaEye, FaFileExport, FaPrint, FaEraser, FaPlay, FaTimes, FaWhatsapp } from 'react-icons/fa';
import AuthContext from "../../../store/auth-context";
import { printPrescription } from "../Prescriptions/PrescriptionPrint";
import { exportPrescriptionPDF } from "../../../util/pdfExportUtil";
import { getReportHeader, getReportStyles } from "../../../util/reportPrintHelper";
import { drawReportHeader, drawAuthorizedSignatory } from "../../../util/headerUtil";
import { exportTableToExcel } from "../../../util/excelExportUtil";
import { Spinner } from "react-bootstrap";

const CheckUpReport = () => {
  const { t } = useTranslation();
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const [totalBill, setTotalBill] = useState(0);
  const [submittedDates, setSubmittedDates] = useState({
    start: getTodayDate(),
    end: getTodayDate(),
  });
  const [selectedPageSize, setSelectedPageSize] = useState("a4");
  const [selectedOrientation, setSelectedOrientation] = useState("portrait");
  const [showPreview, setShowPreview] = useState(false);
  const [data, setData] = useState([]);
  const [exportFormat, setExportFormat] = useState("excel");
  const [isExporting, setIsExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const { hospitalData } = authCtx;

  // Fetch checkup data (only on submit dates change)
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setIsError(false);
      setError(null);

      try {
        const token = getAuthToken();
        if (!token || token === "EXPIRED") {
          navigate("/");
          return;
        }

        const apiChar = getApiChara();
        let apiUrl = `${apiChar}/report/getCheckUpListReportByDate`;
        if (submittedDates.start && submittedDates.end) {
          apiUrl = `${apiChar}/report/getCheckUpListReportByDate?startDate=${submittedDates.start}&endDate=${submittedDates.end}`;
        }

        const response = await fetch(apiUrl, {
          headers: { Authorization: "Bearer " + token },
        });

        if (!response.ok) throw new Error("Network response was not ok");
        const result = await response.json();
        setData(result || []);
      } catch (err) {
        setIsError(true);
        setError(err.message || "Error fetching data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [submittedDates, navigate]);

  // Calculate total bill
  useEffect(() => {
    const total = Array.isArray(data)
      ? data.reduce((sum, item) => sum + (item.bill || 0), 0)
      : 0;
    setTotalBill(total);
  }, [data]);

  const TableHeader = () => (
    <thead className="table-secondary">
      <tr>
        <th>{t("Sr No")}</th>
        <th>{t("Patient Id")}</th>
        <th>{t("Name")}</th>
        <th>{t("Mobile")}</th>
        <th>{t("Appointment")}</th>
        <th>{t("Doctor/Staff")}</th>
        <th>{t("Bill")}</th>
        <th className="text-center">{t("Actions")}</th>
      </tr>
    </thead>
  );

  // PDF Export / Preview
  const generatePDF = () => {
    const pdfDoc = new jsPDF(selectedOrientation, "pt", selectedPageSize);
    const pageWidth = pdfDoc.internal.pageSize.getWidth();
    const textWidth =
      (pdfDoc.getStringUnitWidth("Checkup Reports") *
        pdfDoc.internal.getFontSize()) /
      pdfDoc.internal.scaleFactor;
    const xPos = (pageWidth - textWidth) / 2;

    const startY = drawReportHeader(pdfDoc, hospitalData, "Checkup Reports", { hideSeparator: true, isExport: true, margin: 0, top: 0 });

    pdfDoc.autoTable({
      head: [["Sr No", "Patient Id", "Name", "Mobile", "Appointment", "Doctor/Staff", "Bill"]],
      body: data.map((item, index) => [
        index + 1,
        item.patientId || "-",
        item.patientFullName || "-",
        item.patientMobile || "-",
        item.dateTime || "-",
        item.userName || "-",
        item.bill || 0
      ]),
      startY: startY + 10,
      styles: {
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      }
    });

    drawAuthorizedSignatory(pdfDoc, hospitalData);
    return pdfDoc;
  };

  const handleExport = () => {
    if (exportFormat === "excel") {
      setIsExporting(true);
      setTimeout(() => {
        exportTableToExcel("checkUpReportsTable", "Checkup_Report");
        setIsExporting(false);
      }, 500);
    } else {
      generatePDF().save("checkupreport.pdf");
    }
  };

  const onPrintHandler = handleExport;

  const onPreviewHandler = () => {
    const pdfBlob = generatePDF().output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    setShowPreview(pdfUrl);
  };

  const onGetPrintHandler = () => {
    if (!data || data.length === 0) {
      alert("No data to print");
      return;
    }

    // Create a hidden iframe for printing
    let printFrame = document.getElementById("printFrame");
    if (!printFrame) {
      printFrame = document.createElement("iframe");
      printFrame.id = "printFrame";
      printFrame.style.display = "none";
      document.body.appendChild(printFrame);
    }

    const tableToPrint = document.querySelector("#checkUpReportsTable");
    const tableClone = tableToPrint.cloneNode(true);
    // Remove Action Column from Clone
    const actionHeader = tableClone.querySelector("th:last-child");
    if (actionHeader && actionHeader.innerText.toLowerCase().includes("action")) {
      actionHeader.remove();
      tableClone.querySelectorAll("tr").forEach(tr => {
        const lastCell = tr.querySelector("td:last-child");
        if (lastCell) lastCell.remove();
      });
    }

    const printContent = `
      <html>
        <head>
          <title>Checkup Report - ${new Date().toLocaleDateString()}</title>
          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
          <style>
            ${getReportStyles(selectedPageSize, selectedOrientation)}
          </style>
        </head>
        <body>
          ${getReportHeader(hospitalData, "Checkup Report")}
          <div class="meta-info">
            <div><strong>Date:</strong> ${new Date().toLocaleDateString()}</div>
            <div><strong>Total Records:</strong> ${data.length}</div>
          </div>
          ${tableClone.outerHTML}
        </body>
      </html>
    `;

    const frameDoc = printFrame.contentWindow.document;
    frameDoc.open();
    frameDoc.write(printContent);
    frameDoc.close();

    printFrame.contentWindow.focus();
    setTimeout(() => {
      printFrame.contentWindow.print();
    }, 500);
  };

  const onGetStartEndDateHandler = (start, end) => {
    setSubmittedDates({ start, end });
  };

  const handleRowAction = async (id, action) => {
    try {
      const token = getAuthToken();
      const apiChar = getApiChara();
      setIsLoading(true);
      const response = await fetch(`${apiChar}/checkUp/getCheckUpRecordById?id=${id}`, {
        headers: { Authorization: "Bearer " + token },
      });

      if (!response.ok) throw new Error("Could not fetch checkup details");
      const fullData = await response.json();

      const patientData = fullData;
      const medicines = fullData.prescription;
      const checkedData = {
        diagnosis: fullData.diagnosis,
        weight: fullData.weight,
        bps: fullData.bp1,
        bpd: fullData.bp2,
        pulse: fullData.pulse,
        temp: fullData.temperature,
        spo2: fullData.spo2,
        allergy: fullData.allergy,
        complaint: fullData.complaint,
        advice: fullData.advice,
        billingDate: fullData.dateTime,
        lmp: fullData.lmp || fullData.Lmp || "",
        edd: fullData.edd || fullData.Edd || "",
        hc: fullData.hc || "",
        length: fullData.length || "",
        nextAppointment: fullData.nextAppointment || fullData.next_appointment || ""
      };

      if (action === 'PRINT') {
        printPrescription(patientData, medicines, fullData, checkedData, fullData.dateTime, authCtx);
      } else if (action === 'EXPORT') {
        exportPrescriptionPDF({
          patientData,
          medicines,
          checkedData,
          authCtx,
          selectedDateTime: fullData.dateTime
        });
      } else if (action === 'WHATSAPP') {
        const patientMobile = patientData?.mobile || "";
        const patientName = `${patientData?.firstName || ""} ${patientData?.lastName || ""}`.trim() || t("Patient");
        const cleanMobile = patientMobile.replace(/\D/g, '');

        let medicineText = "";
        if (medicines && medicines.length > 0) {
          medicineText = `\n\n*${t("Prescribed Medicines:")}*`;
          medicines.forEach((med, index) => {
            const type = med.type ? `${med.type}. ` : "";
            const doses = `(${med.breakFastDose || '-'}/${med.lunchDose || '-'}/${med.dinnerDose || '-'})`;
            medicineText += `\n${index + 1}. ${type}${med.medicineName} - ${med.days} ${t("Days")} ${doses}`;
            if (med.instruction) medicineText += ` [${med.instruction}]`;
          });
        }

        const message = t("Hello {{patientName}}, your prescription from {{hospitalName}} is ready.{{medicineText}}\n\nRegards,\n{{hospitalName}}", {
          patientName: patientName,
          hospitalName: authCtx.hospitalData?.hospitalName || t('the hospital'),
          medicineText: medicineText
        });

        const waUrl = `https://wa.me/91${cleanMobile}?text=${encodeURIComponent(message)}`;
        window.open(waUrl, "_blank");
      }
    } catch (err) {
      toast.error(err.message || "Error processing request");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="bg-light p-3 rounded shadow-sm border mb-4 d-flex justify-content-between align-items-center">
        <h5 className="textReports mb-0">{t("Checkup Reports")}</h5>
      </div>
      <Filters onGetStartAndEndDate={onGetStartEndDateHandler} />

      <div id="options" className="mt-4">
        {isLoading ? (
          <Loader />
        ) : isError ? (
          <p className="text-center">{error}</p>
        ) : (
          <div className="printData boxes table-responsive">
            <Table id="checkUpReportsTable" striped bordered hover>
              <TableHeader />
              <tbody>
                {Array.isArray(data) && data.length > 0 ? (
                  <>
                    {data.map((checkupp, index) => (
                      <tr key={checkupp?.checkUpId || index}>
                        <td>{index + 1}</td>
                        <td>
                          {checkupp?.patientId || "-"}
                        </td>
                        <td>
                          {checkupp?.patientFullName || "-"}
                        </td>
                        <td>{checkupp?.patientMobile || "-"}</td>
                        <td>{checkupp?.dateTime || "-"}</td>
                        <td>
                          {checkupp?.userName || "-"}
                        </td>
                        <td>{checkupp?.bill || 0}</td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <Button
                              variant="link"
                              className="text-primary p-0"
                              onClick={() => handleRowAction(checkupp.checkUpId, 'PRINT')}
                              title={t("Print Prescription")}
                            >
                              <FaPrint />
                            </Button>
                            <Button
                              variant="link"
                              className="text-success p-0"
                              onClick={() => handleRowAction(checkupp.checkUpId, 'EXPORT')}
                              title={t("Export PDF")}
                            >
                              <FaFileExport />
                            </Button>
                            <Button
                              variant="link"
                              className="text-success p-0"
                              onClick={() => handleRowAction(checkupp.checkUpId, 'WHATSAPP')}
                              title={t("WhatsApp Share")}
                            >
                              <FaWhatsapp style={{ color: '#25D366' }} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {/* <tr>
                      <td colSpan="5"></td>
                      <td className="text-center">
                        <strong>Total</strong>
                      </td>
                      <td className="text-center">
                        <strong>{totalBill}</strong>
                      </td>
                    </tr> */}
                  </>
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">
                      {t("No Record found")}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}

        {Array.isArray(data) && data.length > 0 && (
          <div className="row options mt-4 bg-light p-3 rounded shadow-sm border mx-0 align-items-end">


            <div className="row g-3 justify-content-center align-items-end">
              <div className="col-auto">
                <label className="fw-bold small d-block">{t("Select Size")}</label>
                <select
                  className="form-select"
                  value={selectedPageSize}
                  onChange={(e) => setSelectedPageSize(e.target.value)}
                  style={{ width: '100px' }}
                >
                  <option value="a2">A2</option>
                  <option value="a3">A3</option>
                  <option value="a4">A4</option>
                  <option value="a5">A5</option>
                </select>
              </div>

              <div className="col-auto">
                <label className="fw-bold small d-block">{t("Orientation")}</label>
                <select
                  className="form-select"
                  value={selectedOrientation}
                  onChange={(e) => setSelectedOrientation(e.target.value)}
                  style={{ width: '130px' }}
                >
                  <option value="portrait">{t("Portrait")}</option>
                  <option value="landscape">{t("Landscape")}</option>
                </select>
              </div>

              <div className="col-auto">
                <button onClick={onGetPrintHandler} className="btn-premium">
                  <FaPrint className="me-2" /> {t("PRINT")}
                </button>
              </div>

              <div className="col-auto">
                <button onClick={onPreviewHandler} className="btn-premium">
                  <FaEye className="me-2" /> {t("PREVIEW")}
                </button>
              </div>

              <div className="col-auto">
                <label className="fw-bold small d-block">{t("Format")}</label>
                <select
                  className="form-select"
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value)}
                  style={{ width: '140px' }}
                >
                  <option value="pdf">{t("PDF Format")}</option>
                  <option value="excel">{t("Excel Format")}</option>
                </select>
              </div>

              <div className="col-auto">
                <button
                  onClick={handleExport}
                  className="btn-export-premium d-flex align-items-center"
                  disabled={isExporting}
                >
                  {isExporting ? <Spinner animation="border" size="sm" className="me-2" /> : <FaFileExport className="me-2" />}
                  {isExporting ? t("EXPORTING...") : t("EXPORT")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showPreview && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.6)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header bg-premium text-white">
                <h5 className="modal-title d-flex align-items-center">
                  <FaEye className="me-2" /> {t("CHECKUP REPORT PREVIEW")}
                </h5>
                <button
                  type="button"
                  className="btn btn-link text-white p-0 border-0"
                  onClick={() => setShowPreview(null)}
                  aria-label="Close"
                  style={{ fontSize: "1.5rem", lineHeight: "1" }}
                >
                  <FaTimes />
                </button>
              </div>
              <div className="modal-body p-0 bg-dark">
                <iframe
                  title="PDF Preview"
                  src={showPreview}
                  width="100%"
                  height="750"
                  frameBorder="0"
                  className="w-100"
                ></iframe>
              </div>
              <div className="modal-footer bg-light p-2">
                <Button className="btn-secondary-premium" onClick={() => setShowPreview(null)}>
                  {t("CLOSE PREVIEW")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckUpReport;
